import React, { useEffect, useRef } from "react";
import { Checkbox as NativeCheckbox, FormControlLabel, FormHelperText } from "@material-ui/core";
import { get, isEmpty } from "lodash";

import FormContext from "components/FormContext";
import BaseFieldInput from "components/inputs/components/base-input-component";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  errorText: {
    color: theme.palette.error.main,
  },
}));

export const Checkbox = ({
  checked,
  onChange,
  disabled,
  label,
  helperText,
  indeterminate,
  required,
  ...other
}) => (
  <FormContext.Consumer>
    {({ formDisabled }) => (
      <BaseFieldInput
        showHelperText={!isEmpty(helperText)}
        helperText={helperText}
        isCheckBox
        {...other}
      >
        {/*
          Plain <div> elements shrink to fit their contents if they don't have a specified width.
          We use this feature to break the chain of width inheritance. As a result:
          - The clickable area of the Checkbox component will be limited to the label and checkbox only
          - It will not extend to the entire width of the parent container
        */}
        <div>
          <FormControlLabel
            disabled={formDisabled || disabled}
            control={(
              <NativeCheckbox
                checked={checked === "indeterminate" ? false : checked}
                onChange={onChange}
                className="checkbox-control"
                indeterminate={checked === "indeterminate"}
              />
            )}
            label={(
              <>
                {/* Conditionally render the star symbol in the label if the required prop is true. */}
                {label} {required && <span style={{ color: "red" }}>*</span>}
              </>
            )}
          />
        </div>
      </BaseFieldInput>
    )}
  </FormContext.Consumer>
);

// Before using CheckboxInput component, please consider using below CheckboxInputValidationAware instead.
const CheckboxInput = ({
  field: { name, value },
  form: { errors, touched, setFieldValue },
  onChange,
  showHelperText,
  required,
  ...other
}) => {
  const inputValue = value === "true" ? true : value;
  const error = get(errors, name, null);
  const isTouched = get(touched, name, false);

  return (
    <Checkbox
      checked={inputValue || false}
      error={!isEmpty(error)}
      helperText={showHelperText && isTouched && error}
      onChange={val => {
        setFieldValue(name, val.target.checked);
        if (onChange) {
          onChange(val.target.checked, setFieldValue);
        }
      }}
      required={required}
      {...other}
    />
  );
};

// CheckboxInputValidationAware supports the autofocus feature, and we should use it instead of CheckboxInput:
// If a field configuration checkbox is set as mandatory and the user tries to submit the form with this checkbox unchecked,
// the form should scroll to and focus on the checkbox.
export const CheckboxInputValidationAware = ({
  field: { name, value },
  form: { errors, touched, setFieldValue, setFieldTouched, submitCount },
  onChange,
  helperText,
  required,
  label,
  disabled,
  ...other
}) => {
  const classes = useStyles();
  const checkboxRef = useRef(null);

  const inputValue = value === "true" ? true : value;
  const error = get(errors, name, null);
  const isTouched = get(touched, name, false);

  useEffect(() => {
    if (error && (isTouched || submitCount > 0) && checkboxRef.current) {
      checkboxRef.current.focus();
    }
  }, [error, isTouched, submitCount]);

  const handleChange = event => {
    const newValue = event.target.checked;
    setFieldValue(name, newValue);
    if (onChange) {
      onChange(newValue, setFieldValue);
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  const showError = (isTouched || submitCount > 0) && !isEmpty(error);
  const errorMessage = typeof error === "string" ? error : "This field is required";

  return (
    <BaseFieldInput
      isCheckBox
      showHelperText={!isEmpty(helperText)}
      helperText={helperText}
      {...other}
    >
      <div>
        <FormControlLabel
          ref={checkboxRef}
          disabled={disabled}
          control={(
            <NativeCheckbox
              checked={inputValue || false}
              onChange={handleChange}
              onBlur={handleBlur}
              className="checkbox-control"
            />
          )}
          label={(
            <>
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </>
          )}
        />
      </div>
      {showError && (
        <FormHelperText className={classes.errorText}>
          {errorMessage}
        </FormHelperText>
      )}
    </BaseFieldInput>
  );
};

export default CheckboxInput;
